import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';
import { StringValidator } from 'src/core/validators/shared/string.validator';

@Component({
    selector: 'ca-ftp-integration-settings',
    templateUrl: './ftp-integration-settings.component.html',
    styleUrls: ['./ftp-integration-settings.component.scss'],
    standalone: false
})
export class FtpIntegrationSettingsComponent implements OnInit {
  form: FormGroup;
  useSftpCb = false;
  ignoreFirstLineCb = false;
  deleteMetadataFileCb = false;
  deleteRecordingFileCb = false;
  selectedPrivateKeyFile: File | null = null;

  private readonly prefix = 'FtpIntegration';
  private readonly sourceName = `${this.prefix}.SourceName`;
  private readonly organizationId = `${this.prefix}.OrganizationId`;
  private readonly host = `${this.prefix}.Host`;
  private readonly port = `${this.prefix}.Port`;
  private readonly username = `${this.prefix}.Username`;
  private readonly password = `${this.prefix}.Password`;
  private readonly useSftp = `${this.prefix}.UseSftp`;
  private readonly extension = `${this.prefix}.Extension`;
  private readonly path = `${this.prefix}.Path`;
  private readonly errorPath = `${this.prefix}.ErrorPath`;
  private readonly ignoreFirstLine = `${this.prefix}.IgnoreFirstLine`;
  private readonly template = `${this.prefix}.Template`;
  private readonly deleteMetadataFile = `${this.prefix}.DeleteMetadataFile`;
  private readonly deleteRecordingFile = `${this.prefix}.DeleteRecordingFile`;

  constructor(
    private fb: FormBuilder,
    private config: ConfigStateService,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private localizationService: LocalizationService
  ) {
    this.useSftpCb = config.getSetting(this.useSftp) == 'true';
    this.ignoreFirstLineCb = config.getSetting(this.ignoreFirstLine) == 'true';
    this.deleteMetadataFileCb = config.getSetting(this.deleteMetadataFile) == 'true';
    this.deleteRecordingFileCb = config.getSetting(this.deleteRecordingFile) == 'true';

    this.form = this.fb.group({
      sourceName: this.fb.group({
        name: this.sourceName,
        value: [
          this.config.getSetting(this.sourceName),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      organizationId: this.fb.group({
        name: this.organizationId,
        value: [
          this.config.getSetting(this.organizationId),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      host: this.fb.group({
        name: this.host,
        value: [
          this.config.getSetting(this.host),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      port: this.fb.group({
        name: this.port,
        value: [
          this.config.getSetting(this.port),
          {
            validators: [Validators.required, Validators.pattern('^[0-9]+$')],
          },
        ],
      }),
      username: this.fb.group({
        name: this.username,
        value: [
          this.config.getSetting(this.username),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      password: this.fb.group({
        name: this.password,
        value: [
          this.config.getSetting(this.password),
          {
            validators: [StringValidator.whitespace],
          },
        ],
      }),
      useSftp: this.fb.group({
        name: this.useSftp,
        value: [
          this.config.getSetting(this.useSftp),
        ],
      }),
      extension: this.fb.group({
        name: this.extension,
        value: [
          this.config.getSetting(this.extension),
          {
            validators: [StringValidator.whitespace],
          },
        ],
      }),
      path: this.fb.group({
        name: this.path,
        value: [
          this.config.getSetting(this.path),
          {
            validators: [StringValidator.whitespace],
          },
        ],
      }),
      errorPath: this.fb.group({
        name: this.errorPath,
        value: [
          this.config.getSetting(this.errorPath),
          {
            validators: [StringValidator.whitespace],
          },
        ],
      }),
      ignoreFirstLine: this.fb.group({
        name: this.ignoreFirstLine,
        value: [
          this.config.getSetting(this.ignoreFirstLine),
        ],
      }),
      template: this.fb.group({
        name: this.template,
        value: [
          this.config.getSetting(this.template),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      deleteMetadataFile: this.fb.group({
        name: this.deleteMetadataFile,
        value: [
          this.config.getSetting(this.deleteMetadataFile),
        ],
      }),
      deleteRecordingFile: this.fb.group({
        name: this.deleteRecordingFile,
        value: [
          this.config.getSetting(this.deleteRecordingFile),
        ],
      }),
    });
  }

  ngOnInit(): void {}

  onSubmitForm(eventArgs): void {
    if (!this.form.valid) {
      return;
    }

    const settings: ConfigurationSettingsDto[] = [];

    settings.push({
      settingName: this.form.get('sourceName').get('name').value,
      settingValue: this.form.get('sourceName').get('value').value,
    });

    settings.push({
      settingName: this.form.get('organizationId').get('name').value,
      settingValue: this.form.get('organizationId').get('value').value,
    });

    settings.push({
      settingName: this.form.get('host').get('name').value,
      settingValue: this.form.get('host').get('value').value,
    });

    settings.push({
      settingName: this.form.get('port').get('name').value,
      settingValue: this.form.get('port').get('value').value,
    });

    settings.push({
      settingName: this.form.get('username').get('name').value,
      settingValue: this.form.get('username').get('value').value,
    });

    if (this.form.get('password').get('value').value) {
      settings.push({
        settingName: this.form.get('password').get('name').value,
        settingValue: this.form.get('password').get('value').value,
      });
    }

    settings.push({
      settingName: this.form.get('useSftp').get('name').value,
      settingValue: this.useSftpCb.toString(),
    });

    settings.push({
      settingName: this.form.get('extension').get('name').value,
      settingValue: this.form.get('extension').get('value').value,
    });

    settings.push({
      settingName: this.form.get('path').get('name').value,
      settingValue: this.form.get('path').get('value').value,
    });

    settings.push({
      settingName: this.form.get('errorPath').get('name').value,
      settingValue: this.form.get('errorPath').get('value').value,
    });

    settings.push({
      settingName: this.form.get('ignoreFirstLine').get('name').value,
      settingValue: this.ignoreFirstLineCb.toString(),
    });

    settings.push({
      settingName: this.form.get('template').get('name').value,
      settingValue: this.form.get('template').get('value').value,
    });

    settings.push({
      settingName: this.form.get('deleteMetadataFile').get('name').value,
      settingValue: this.deleteMetadataFileCb.toString(),
    });

    settings.push({
      settingName: this.form.get('deleteRecordingFile').get('name').value,
      settingValue: this.deleteRecordingFileCb.toString(),
    });

    this.settingService.saveSetting(settings).subscribe(res => {
      this.toastr.success(
        this.localizationService.instant('AbpSettingManagement::SavedSuccessfully')
      );

      if (this.useSftpCb && this.selectedPrivateKeyFile) {
        this.settingService.uploadSftpPrivateKey(this.selectedPrivateKeyFile).subscribe({
          next: () => {
            setTimeout(() => {
              this.toastr.success(
                this.localizationService.instant('Settings::CertificateImported')
              );
            }, 100);

            this.selectedPrivateKeyFile = null;
          },
          error: () => {
            setTimeout(() => {
              this.toastr.error(
                this.localizationService.instant('Settings::CertificateImportFailed')
              );
            }, 100);
          },
        });
      }
    });
  }

  onImportPrivateKeyClick(e: MouseEvent) {
    const el = document.getElementById('sftpPrivateKeySelector') as HTMLElement;
    el.click();
  }

  onImportPrivateKey(files: FileList) {
    if (!files || files.length === 0) {
      return;
    }

    const file = files.item(0);
    if (!file) {
      return;
    }

    this.selectedPrivateKeyFile = file;
  }
}
